import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload, Gift, Pencil, Trash2 } from 'lucide-react';
import { queryKeys } from '@/lib/api/queryKeys';
import { stockApi } from '@/features/stock/api';
import type { XkStock } from '@/lib/api/types';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { toast } from '@/components/ui/Toast';
import { Tooltip } from '@/components/ui/Tooltip';
import { usePagination } from '@/lib/hooks/usePagination';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';
import { type NewXotski } from '@/features/stock/AddXotskiDialog';
import { BulkImportDialog } from '@/features/stock/import/BulkImportDialog';
import { useFormPanel } from '@/components/layout/formPanelStore';
import { NextStepsGuide } from './NextStepsGuide';
import { WarehousePanel, RecentMovements } from '@/features/stock/WarehousePanel';
import { useWarehouse } from '@/features/stock/useWarehouse';

/**
 * Unified xotski + inventory surface for a brand.
 *
 * Combines what used to be two separate tabs:
 *  · "Xotskies"  — list / add / bulk-import the brand's catalog.
 *  · "Inventory" — counters (total / allocated / delivered / available),
 *                  inline restock, adopt-from-agency.
 *
 * The card now carries everything in its footer so operators can restock
 * without entering the edit dialog.
 */
export function XotskisTab({
  brandId,
  agencyId,
}: {
  brandId: number | null;
  /** Parent agency — null for top-level companies (no master catalog). */
  agencyId: number | null;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const openForm = useFormPanel((s) => s.openForm);
  const [importOpen, setImportOpen] = useState(false);

  // Create/edit render in the right-side panel (assistant rail), not a modal.
  const openAdd = () =>
    openForm({
      kind: 'xotski',
      mode: 'create',
      title: t('stock.dialog.addingTitle'),
      context: { brandId },
    });
  const openEdit = (s: XkStock) =>
    openForm({
      kind: 'xotski',
      mode: 'edit',
      title: t('stock.dialog.editingTitle'),
      context: { brandId, stockId: s.id, initial: stockToInitial(s) },
    });

  const stocksQ = useQuery({
    queryKey: queryKeys.stock.byBrand(brandId ?? 0),
    queryFn: () => stockApi.list({ companyId: brandId }),
    enabled: !!brandId,
  });


  // Los cuatro contadores salen del almacén (la misma fuente que el panel de
  // My Merch y el informe de cada evento), no de una lista de 25 asignaciones.
  const brandStocks = useMemo(() => stocksQ.data?.items ?? [], [stocksQ.data]);
  const { perStock } = useWarehouse(brandId ?? null, brandStocks);

  // Parent agency's master catalog — surface as an "available to adopt"
  // section below the brand grid. Skipped when the brand has no parent.
  const agencyCatalogQ = useQuery({
    queryKey: ['agency-catalog', agencyId],
    queryFn: () => stockApi.agencyCatalog(agencyId!),
    enabled: !!agencyId && agencyId !== brandId,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.stock.byBrand(brandId ?? 0),
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.stock.all });
    queryClient.invalidateQueries({
      queryKey: queryKeys.stockAllocations.byBrand(brandId ?? 0),
    });
    queryClient.invalidateQueries({ queryKey: ['insights'] });
  };

  const remove = useMutation({
    mutationFn: (id: number) => stockApi.remove(id),
    onSuccess: () => invalidateAll(),
    // Backend rejects when the row still has active allocations on a
    // station. Surface that message so the operator knows why the delete
    // didn't go through — the trash icon is also disabled client-side
    // for the same reason (see BrandXotskiCard.canRemove).
    onError: (e: Error) =>
      toast({
        tone: 'error',
        title: t('brand.xotskies.deleteBlocked'),
        description: e.message,
      }),
  });

  const restock = useMutation({
    mutationFn: ({ stockId, delta }: { stockId: number; delta: number }) =>
      stockApi.restock(stockId, delta),
    onSuccess: () => {
      invalidateAll();
      toast({ tone: 'success', title: 'Restocked' });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: 'Restock failed', description: e.message }),
  });

  const adopt = useMutation({
    mutationFn: ({
      agencyStockId,
      initialQuantity,
    }: {
      agencyStockId: number;
      initialQuantity: number;
    }) =>
      stockApi.adopt(agencyStockId, {
        brandId: brandId!,
        initialQuantity,
      }),
    onSuccess: () => {
      invalidateAll();
      toast({ tone: 'success', title: 'Prize adopted' });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: 'Adopt failed', description: e.message }),
  });

  const items = stocksQ.data?.items ?? [];
  const pager = usePagination(items, 12);

  // Hide already-adopted master prizes from the agency catalog.
  const adoptedSourceIds = useMemo(
    () =>
      new Set(
        items
          .map((s) => s.sourceStockId)
          .filter((x): x is number => x != null),
      ),
    [items],
  );
  const agencyCatalog = (agencyCatalogQ.data ?? []).filter(
    (s) => !adoptedSourceIds.has(s.id),
  );

  if (!brandId) {
    return (
      <EmptyState
        icon={<Gift className="h-6 w-6" />}
        title={t('brand.xotskies.noBrand')}
        description={t('brand.xotskies.noBrandBody')}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — Add + Bulk Import */}
      {/* El almacén de la marca (maqueta «My Merch»): qué hay, lotes sin destino
          con su disposición. La misma fuente que los contadores de abajo. */}
      <WarehousePanel brandId={brandId ?? null} stocks={brandStocks} onEditStock={openEdit} />

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3
            className="text-base font-black tracking-tight text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t('brand.xotskies.title')}
          </h3>
          <p className="mt-0.5 text-xs text-[var(--color-xk-text-muted)]">
            {t('brand.xotskies.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-tour="merch-import"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-xk-border)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-accent-ink)] hover:bg-[var(--color-xk-surface)]"
          >
            <Upload className="h-3.5 w-3.5" />
            {t('brand.xotskies.import')}
          </button>
          <span data-tour="merch-create">
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={openAdd}
            >
              {t('brand.xotskies.add')}
            </Button>
          </span>
        </div>
      </header>

      {/* Grid */}
      {stocksQ.isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Gift className="h-6 w-6" />}
          title={t('brand.xotskies.emptyTitle')}
          description={t('brand.xotskies.emptyBody')}
        />
      ) : (
        <>
          <div data-tour="merch-list" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pager.pageItems.map((s) => {
              const counters = perStock.get(s.id);
              const total = s.initialQuantity ?? 0;
              // Asignado = en juego en eventos abiertos; Entregado = libro de
              // premios; Disponible = lo que la marca posee ahora (almacén +
              // en juego), como dice la maqueta.
              const allocated = counters?.committed ?? 0;
              const delivered = counters?.delivered ?? 0;
              const available = s.quantityRemaining ?? 0;
              return (
                <BrandXotskiCard
                  key={s.id}
                  stock={s}
                  total={total}
                  allocated={allocated}
                  delivered={delivered}
                  available={available}
                  onEdit={() => openEdit(s)}
                  onDelete={() => remove.mutate(s.id)}
                  onRestock={(delta) =>
                    restock.mutate({ stockId: s.id, delta })
                  }
                  restockPending={restock.isPending}
                />
              );
            })}
          </div>
          <Pagination {...pager} noun="xotskies" />
        </>
      )}

      {/* Agency catalog — adopt-from-parent action */}
      {agencyId && agencyId !== brandId && (
        <section className="space-y-2 border-t border-[var(--color-xk-border)] pt-5">
          <h4
            className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t('brand.xotskies.agencyCatalog')}
          </h4>
          {agencyCatalogQ.isLoading ? (
            <Skeleton className="h-16 rounded-lg" />
          ) : agencyCatalog.length === 0 ? (
            <p className="text-[12px] text-[var(--color-xk-text-muted)]">
              {t('brand.xotskies.agencyCatalogEmpty')}
            </p>
          ) : (
            <div className="space-y-2">
              {agencyCatalog.map((master) => (
                <AgencyCatalogRow
                  key={master.id}
                  master={master}
                  onAdopt={(qty) =>
                    adopt.mutate({
                      agencyStockId: master.id,
                      initialQuantity: qty,
                    })
                  }
                  isPending={adopt.isPending}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Bulk import stays a modal — it's a one-off paste/upload flow. */}
      <RecentMovements />

      <BulkImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        existingStock={items}
      />

      {/*
        La guía de los primeros días, debajo del catálogo.
        Aquí y no en el panel: subir el merch es el primer paso, y al terminarlo
        la pantalla se ve llena y parece que ya está — cuando lo que falta es
        justo lo siguiente. Se apaga sola a la semana y en cuanto está hecho.
      */}
      <NextStepsGuide brandId={brandId} tieneMerch={items.length > 0} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Card with inventory counters + inline restock
// ────────────────────────────────────────────────────────────────────────────

function BrandXotskiCard({
  stock,
  total,
  allocated,
  delivered,
  available,
  onEdit,
  onDelete,
  onRestock,
  restockPending,
}: {
  stock: XkStock;
  total: number;
  allocated: number;
  delivered: number;
  available: number;
  onEdit: () => void;
  onDelete: () => void;
  onRestock: (delta: number) => void;
  restockPending: boolean;
}) {
  const t = useT();
  const [delta, setDelta] = useState(0);
  const rating = Math.max(1, Math.min(5, stock.rating ?? 3));
  // Existence guard: a xotski with active allocations on any station
  // cannot be removed — the relationship is enforced by the backend
  // (and reflected here to keep the UI honest before the round-trip).
  const canRemove = allocated === 0;
  // Negative delta = write-off. Block when it would reduce remaining
  // below what's already allocated to stations.
  const writeOffOverflow = delta < 0 && -delta > available;
  const canRestock =
    !restockPending && delta !== 0 && !writeOffOverflow;
  const initials = useMemo(() => {
    const parts = stock.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '◇';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }, [stock.name]);

  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] transition-colors hover:border-[var(--color-accent-ink)]">
      {/* Image header */}
      <div className="relative h-28 overflow-hidden bg-[var(--color-xk-bg-deep)]">
        {stock.image ? (
          <img
            src={resolveImageUrl(stock.image)}
            alt={stock.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[var(--color-xk-text-muted)]">
            <span
              className="text-base font-black tracking-widest text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {initials}
            </span>
          </div>
        )}
        {/* Xotski logo badge — top-left of the photo. Falls back to a
            monogram tile so the slot is always populated even before a
            logo is picked. */}
        <div className="absolute left-2 top-2 grid h-9 w-9 place-items-center overflow-hidden rounded-md border border-white/20 bg-[rgb(0_0_0/0.55)] p-1 shadow backdrop-blur-sm">
          {stock.logo ? (
            <img
              src={resolveImageUrl(stock.logo)}
              alt=""
              className="h-full w-full object-contain"
              aria-hidden
            />
          ) : (
            <span
              className="text-[10px] font-black tracking-wide text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
              aria-hidden
            >
              {initials}
            </span>
          )}
        </div>
        <div className="absolute right-2 top-2 flex gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onClick={onEdit}
            aria-label="Edit"
            className="rounded-md bg-[rgb(0_0_0/0.6)] p-1 text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent)] hover:text-[var(--color-fg-on-accent)]"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={!canRemove}
            aria-label={
              canRemove
                ? t('brand.xotskies.deleteAria')
                : t('brand.xotskies.deleteBlockedHint')
            }
            title={
              canRemove
                ? t('brand.xotskies.deleteAria')
                : t('brand.xotskies.deleteBlockedHint')
            }
            className="rounded-md bg-[rgb(0_0_0/0.6)] p-1 text-white transition-colors hover:bg-[var(--color-error)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-[rgb(0_0_0/0.6)]"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-2 p-3">
        <h4
          className="line-clamp-1 text-sm font-extrabold tracking-tight text-[var(--color-xk-lavender)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {stock.name}
        </h4>
        <Stars value={rating} />

        {/*
          Los cuatro contadores.
        
          Iban con la etiqueta en inglés a fuego y ABREVIADA —«Alloc.»,
          «Deliv.»— así que ni se traducían ni se entendían: cuatro números sin
          explicación de qué distingue a uno de otro. Cada uno lleva ahora su
          descripción al pasar por encima, en el idioma de la aplicación. Va en
          tooltip y no en la propia tarjeta porque son cuatro celdas de 9 px en
          una rejilla de cuatro columnas: cabe el número, no la frase.
        */}
        <div className="grid grid-cols-4 gap-1.5 border-t border-[var(--color-xk-border)] pt-2 text-center text-[9px] uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          <Counter clave="total" value={total} />
          <Counter clave="allocated" value={allocated} />
          <Counter clave="delivered" value={delivered} />
          <Counter clave="available" value={available} accent />
        </div>

        {/* Inline restock — `+ units` adds, negative writes off.
            Write-offs are clamped to `available`: we never let the user
            reduce remaining below what's already allocated. */}
        <div>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              step={1}
              value={delta || ''}
              placeholder="+ units"
              onChange={(e) =>
                setDelta(parseInt(e.target.value || '0', 10) || 0)
              }
              aria-invalid={writeOffOverflow}
              className={
                writeOffOverflow
                  ? 'w-20 rounded-md border border-[var(--color-error)] bg-[var(--color-xk-bg-deep)] py-1 text-center text-[12px] text-[var(--color-xk-lavender)]'
                  : 'w-20 rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] py-1 text-center text-[12px] text-[var(--color-xk-lavender)]'
              }
            />
            <button
              type="button"
              title={
                writeOffOverflow
                  ? t('brand.xotskies.writeOffBlocked').replace(
                      '{available}',
                      String(available),
                    )
                  : undefined
              }
              className="flex-1 rounded-md bg-[var(--color-xk-accent)] px-2 py-1 text-[11px] font-bold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!canRestock}
              onClick={() => {
                onRestock(delta);
                setDelta(0);
              }}
            >
              Restock
            </button>
          </div>
          {writeOffOverflow && (
            <p className="mt-1 text-[10px] text-[var(--color-error)]">
              {t('brand.xotskies.writeOffBlocked').replace(
                '{available}',
                String(available),
              )}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * Un contador del inventario de la marca, con su explicación.
 *
 * `clave` es la del contador (`total`, `allocated`…), no un texto: de ella
 * salen la etiqueta corta y la descripción larga, las dos traducidas. Pasar el
 * texto ya resuelto era lo que había, y por eso estaban en inglés.
 */
function Counter({
  clave,
  value,
  accent,
}: {
  clave: 'total' | 'allocated' | 'delivered' | 'available';
  value: number;
  accent?: boolean;
}) {
  const t = useT();
  return (
    <Tooltip
      label={t(`brand.xotskies.counter.${clave}.label`)}
      description={t(`brand.xotskies.counter.${clave}.help`)}
    >
      <div
        // `cursor-help` es la señal de que hay algo que leer aquí: sin ella el
        // tooltip existe y nadie lo busca.
        className={
          accent
            ? 'cursor-help rounded bg-[var(--color-accent-15)] p-1'
            : 'cursor-help rounded bg-[var(--color-xk-bg-deep)] p-1'
        }
      >
        <div
          className={
            accent
              ? 'text-[12px] font-extrabold text-[var(--color-accent-ink)]'
              : 'text-[12px] font-extrabold text-[var(--color-xk-lavender)]'
          }
        >
          {value}
        </div>
        <div className="mt-0.5 text-[8px] font-bold">
          {t(`brand.xotskies.counter.${clave}.label`)}
        </div>
      </div>
    </Tooltip>
  );
}

function Stars({ value }: { value: number }) {
  return (
    <div className="flex items-center" aria-label={`Rating ${value}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={
            i <= value
              ? 'text-[var(--color-accent-ink)]'
              : 'text-[var(--color-xk-border)]'
          }
        >
          ★
        </span>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Agency catalog row — adopt-from-parent
// ────────────────────────────────────────────────────────────────────────────

function AgencyCatalogRow({
  master,
  onAdopt,
  isPending,
}: {
  master: XkStock;
  onAdopt: (qty: number) => void;
  isPending: boolean;
}) {
  const [qty, setQty] = useState(10);
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-[var(--color-xk-border)] p-2.5">
      <div className="flex items-center gap-3">
        <div className="grid h-9 w-9 flex-none place-items-center overflow-hidden rounded bg-[var(--color-xk-bg-deep)]">
          {master.image || master.logo ? (
            <img
              src={resolveImageUrl(master.logo ?? master.image ?? '')}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <Gift className="h-4 w-4 text-[var(--color-xk-text-muted)]" />
          )}
        </div>
        <div className="flex-1 truncate text-[13px] font-semibold text-[var(--color-xk-lavender)]">
          {master.name}
        </div>
        <Stars value={Math.max(1, Math.min(5, master.rating ?? 3))} />
        {!open ? (
          <button
            type="button"
            className="rounded-md border border-[var(--color-accent-ink)] px-3 py-1 text-[11px] font-bold text-[var(--color-accent-ink)] hover:bg-[var(--color-accent-10)]"
            onClick={() => setOpen(true)}
            disabled={isPending}
          >
            Adopt
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              step={1}
              value={qty}
              onChange={(e) =>
                setQty(Math.max(1, parseInt(e.target.value || '0', 10) || 1))
              }
              className="w-20 rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] py-1 text-center text-[12px] text-[var(--color-xk-lavender)]"
            />
            <button
              type="button"
              className="rounded-md bg-[var(--color-xk-accent)] px-3 py-1 text-[11px] font-bold text-[var(--color-fg-on-accent)] disabled:opacity-50"
              disabled={isPending || qty <= 0}
              onClick={() => {
                onAdopt(qty);
                setOpen(false);
              }}
            >
              Confirm
            </button>
            <button
              type="button"
              className="text-[11px] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function stockToInitial(s: XkStock): NewXotski {
  return {
    name: s.name,
    description: s.description ?? undefined,
    translate: s.translate ?? undefined,
    image: s.image ?? undefined,
    logo: s.logo ?? undefined,
    unitCost: s.unitCost != null ? Number(s.unitCost) : undefined,
    initialQuantity: s.initialQuantity ?? undefined,
    classification: s.classification ?? undefined,
    rating: s.rating ?? 3,
    restockThreshold: s.restockThreshold ?? undefined,
  };
}
